// src/components/signage/CampaignPreview.js
// Mini preview viewer for campaigns — loops automatically, no user controls (just dots)
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchMediaAsBlob } from '../../services/signageService';

/**
 * CampaignPreview - Mini slideshow preview
 * @param {Object} props
 * @param {Object} props.campaign - Campaign object with slides array
 * @param {string} props.maxWidth - Optional max width override
 */
function CampaignPreview({ campaign, maxWidth }) {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [resolvedSlides, setResolvedSlides] = useState([]);
    const timerRef = useRef(null);
    const videoRef = useRef(null);

    // Pre-fetch all slide media via authenticated requests
    useEffect(() => {
        if (!campaign?.slides?.length) { setResolvedSlides([]); return; }
        let cancelled = false;
        Promise.all(campaign.slides.map(async slide => ({
            ...slide,
            src: await fetchMediaAsBlob(slide.src),
        }))).then(resolved => {
            if (!cancelled) setResolvedSlides(resolved);
        });
        return () => { cancelled = true; };
    }, [campaign]);

    const slides = resolvedSlides;
    const orientation = campaign?.orientation || 'landscape';

    const goToNextSlide = useCallback(() => {
        setCurrentSlideIndex(prev => {
            const next = prev + 1;
            return next >= slides.length ? 0 : next;
        });
    }, [slides.length]);

    // Timer for image slides
    useEffect(() => {
        if (slides.length <= 1) return;

        const currentSlide = slides[currentSlideIndex];
        if (!currentSlide || currentSlide.type === 'video') return;

        const duration = (currentSlide.duration || 5) * 1000;
        timerRef.current = setTimeout(goToNextSlide, duration);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [currentSlideIndex, slides, goToNextSlide]);

    const handleVideoEnded = useCallback(() => {
        goToNextSlide();
    }, [goToNextSlide]);

    if (!campaign || slides.length === 0) {
        return (
            <div
                className={`campaign-preview-mini orientation-${orientation}`}
                style={maxWidth ? { maxWidth } : undefined}
            >
                <span style={{ color: '#666', fontSize: '0.85rem' }}>Sin slides</span>
            </div>
        );
    }

    const currentSlide = slides[currentSlideIndex];

    return (
        <div
            className={`campaign-preview-mini orientation-${orientation}`}
            style={maxWidth ? { maxWidth } : undefined}
        >
            {currentSlide.type === 'video' ? (
                <video
                    ref={videoRef}
                    src={currentSlide.src}
                    autoPlay
                    muted
                    onEnded={handleVideoEnded}
                    style={{ pointerEvents: 'none' }}
                />
            ) : (
                <img
                    src={currentSlide.src}
                    alt={currentSlide.filename || `Slide ${currentSlideIndex + 1}`}
                    draggable={false}
                />
            )}

            {slides.length > 1 && (
                <div className="preview-controls">
                    {slides.map((_, i) => (
                        <div
                            key={i}
                            className={`preview-dot ${i === currentSlideIndex ? 'active' : ''}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default CampaignPreview;
